import { DateTime } from 'luxon'
import { useQueryClient } from 'react-query'
import {
  BatchProgramEventOperation,
  ErrorResponse,
  ProgramEventList,
  ProgramEvent,
  ProgramEventBulkParams,
  EventsApi,
  ProgramEventPatchParams,
} from '@citruscamps/citrus-client'
import { useFlags } from '../../../../../components/FeatureFlag/hooks/useFlags'
import { useToast } from '../../../../../hooks/useToast'
import { sendEvent } from '../../../../../lib/gtag'
import translations from '../../../../../translations/en.json'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { EventDescriptionSchema } from '../../EventDetails/schemas/EventDescriptionSchema'
import { EventFilterProps } from './useFetchEvents'
import { EventSchema } from '../../../schemas/EventSchema'

class LocalError implements ErrorResponse {
  constructor({ type, code, message, param, path, timestamp }: Partial<ErrorResponse>) {
    this.type = type || 'invalid_input_error'
    this.code = code || 400
    this.message = message || ''
    this.param = param || ''
    this.path = path || ''
    this.timestamp = timestamp || new Date().toJSON()
  }
  type: string
  code: number
  message: string
  param: string
  path: string
  timestamp: string
}

interface IProps {
  programId?: string
  search?: string
  filter?: EventFilterProps
}

interface IUpdateEvents {
  handlePublish: (event: ProgramEvent) => Promise<void>
  handleBulkPublish: (events: ProgramEvent[], update_all?: boolean) => Promise<void>
  handleUnpublish: (event: ProgramEvent) => Promise<void>
  handleBulkUnpublish: (events: ProgramEvent[], update_all?: boolean) => Promise<void>
  handleCancel: (event: ProgramEvent) => Promise<void>
  handleBulkCancel: (events: ProgramEvent[], update_all?: boolean) => Promise<void>
  handleDelete: (event: ProgramEvent) => Promise<void>
  handleBulkDelete: (events: ProgramEvent[], update_all?: boolean) => Promise<void>
  handlePatch: (id: string, event: ProgramEventPatchParams) => Promise<void>
  handleBulkPatch: (
    values: ProgramEventBulkParams,
  ) => Promise<BatchProgramEventOperation | undefined>
}

export const useUpdateEvents = ({ programId, search, filter }: IProps): IUpdateEvents => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const { flags } = useFlags()
  const { setToast } = useToast()
  const validateCount = async (
    event: ProgramEvent,
    max_monthly_camps: number,
  ): Promise<string | undefined> => {
    const client = new EventsApi(generateApiConfig())
    const startsAt: DateTime = DateTime.fromJSDate(new Date(event.starts_at))
    if (!programId) {
      throw new Error('Events could not be found due to missing program id.')
    }
    const { total: startTotal }: ProgramEventList = await requestHandler<ProgramEventList>(
      () =>
        client.findProgramEvents({
          program_id: programId,
          limit: 0,
          status: ['live'],
          scheduled_lte: startsAt.endOf('month').endOf('day').toJSDate(),
          scheduled_gte: startsAt.startOf('month').startOf('day').toJSDate(),
        }),
      {
        toast: { setToast },
      },
    )
    const endsAt: DateTime = DateTime.fromJSDate(new Date(event.ends_at))
    const { total: endTotal }: ProgramEventList = await requestHandler<ProgramEventList>(
      () =>
        client.findProgramEvents({
          program_id: programId,
          limit: 0,
          status: ['live'],
          scheduled_lte: endsAt.endOf('month').endOf('day').toJSDate(),
          scheduled_gte: endsAt.startOf('month').startOf('day').toJSDate(),
        }),
      {
        toast: { setToast },
      },
    )
    if (startTotal >= max_monthly_camps) {
      return (translations as any)['error.publish_event_monthly_max']
        .split(':amount')
        .join(startTotal)
        .split(':month')
        .join(startsAt.toFormat('MMMM'))
    } else if (endTotal >= max_monthly_camps) {
      return (translations as any)['error.publish_event_monthly_max']
        .split(':amount')
        .join(endTotal)
        .split(':month')
        .join(endsAt.toFormat('MMMM'))
    }
  }

  const handlePublish = async (value: ProgramEvent) => {
    if (value && flags?.max_monthly_camps) {
      const errorMessage = await validateCount(value, flags?.max_monthly_camps)
      if (errorMessage) {
        setToast?.({
          header: (translations as any)['error.default_toast_header'],
          message: errorMessage,
          type: 'danger',
        })
        return
      }
    }
    const isValidEventLogistics: boolean =
      !flags || EventSchema.get({ schema: 'logistics', features: flags }).isValidSync(value)
    const isValidEventDescription: boolean = EventDescriptionSchema.isValidSync(value)
    const isValidEventPricingOptions: boolean = !!value?.capacity && value?.capacity > 0
    const isValid: boolean =
      isValidEventLogistics && isValidEventDescription && isValidEventPricingOptions
    if (value?.status === 'draft' && isValid && programId) {
      const client = new EventsApi(generateApiConfig())
      try {
        const item = await requestHandler<ProgramEvent>(
          () => client.publishProgramEvent({ program_id: programId, event_id: value.id }),
          {
            toast: {
              success: (translations as any)['success.publish_event_message'],
              setToast,
            },
          },
        )
        sendEvent({
          label: 'Publish',
          category: 'Lead',
          value: item.id,
          event_name: item.name,
          step: 'publish_event',
          action: 'setup',
        })
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'program_event', program_id: programId }),
        )
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.publish_event_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      let param: string = 'dashboard'
      if (!isValidEventLogistics) {
        param = 'options'
      }
      if (!isValidEventDescription) {
        param = 'details'
      }
      if (!isValidEventPricingOptions) {
        param = 'logistics'
      }
      throw new LocalError({
        message: (translations as any)['error.default_inputs_message'],
        param,
      })
    }
  }

  const handleBulkPublish = async (values: ProgramEvent[], update_all?: boolean) => {
    if (programId && (values.length > 0 || update_all)) {
      if (flags?.max_monthly_camps) {
        const errorMessage = await validateCount(values[0], flags?.max_monthly_camps)
        if (errorMessage) {
          setToast?.({
            header: (translations as any)['error.default_toast_header'],
            message: errorMessage,
            type: 'danger',
          })
          return
        }
      }
      const client = new EventsApi(generateApiConfig())
      try {
        const operation = await requestHandler<BatchProgramEventOperation>(() =>
          client.publishBulkProgramEvent({
            program_id: programId,
            ProgramEventBulkParams: {
              update_all,
              event_ids: !update_all ? values.map((e) => e.id) : [],
            },
            search: search || undefined,
            age_lt: filter?.age?.lt,
            age_gt: filter?.age?.gt,
            age_lte: filter?.age?.lte,
            age_gte: filter?.age?.gte,
            gender: filter?.gender && filter?.gender.length > 0 ? filter.gender : undefined,
            venue_city:
              filter?.venue_city && filter?.venue_city.length > 0 ? filter.venue_city : undefined,
            online_event:
              typeof filter?.online_event !== 'undefined' ? filter?.online_event : undefined,
            status: filter?.status && filter?.status.length > 0 ? filter.status : undefined,
            type: filter?.type && filter?.type.length > 0 ? filter.type : undefined,
            tags: filter?.tags && filter?.tags.length > 0 ? filter.tags : undefined,
            id: filter?.id && filter?.id.length > 0 ? filter.id : undefined,
            is_appointment:
              typeof filter?.is_appointment !== 'undefined' ? filter.is_appointment : undefined,
            capacity_lt: filter?.capacity?.lt,
            capacity_gt: filter?.capacity?.gt,
            capacity_lte: filter?.capacity?.lte,
            capacity_gte: filter?.capacity?.gte,
            scheduled_lt:
              filter?.scheduled?.lt &&
              !filter?.scheduled_type?.includes('is upcoming') &&
              !filter?.scheduled_type?.includes('is past')
                ? new Date(filter?.scheduled?.lt)
                : undefined,
            scheduled_gt:
              filter?.scheduled?.gt &&
              !filter?.scheduled_type?.includes('is upcoming') &&
              !filter?.scheduled_type?.includes('is past')
                ? new Date(filter?.scheduled?.gt)
                : undefined,
            scheduled_lte: filter?.scheduled_type?.includes('is past')
              ? new Date()
              : filter?.scheduled?.lte
              ? new Date(filter?.scheduled?.lte)
              : undefined,
            scheduled_gte: filter?.scheduled_type?.includes('is upcoming')
              ? new Date()
              : filter?.scheduled?.gte
              ? new Date(filter?.scheduled?.gte)
              : undefined,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'program_event', program_id: programId }),
        )
        if (operation.errored_operations === 0) {
          setToast?.({
            header: (translations as any)['success.default_toast_header'],
            message: (translations as any)['success.default_toast_message'],
            type: 'success',
          })
        } else {
          setToast?.({
            header: (translations as any)['error.default_toast_header'],
            message: (translations as any)['error.update_bulk_events'],
            type: 'danger',
          })
        }
        if (operation.finished_operations > 0) {
          sendEvent({
            label: 'Publish',
            category: 'Lead',
            value: operation.items[0].data.id,
            event_name: operation.items[0].data.name,
            step: 'publish_event',
            action: 'setup',
          })
        }
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.default_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleUnpublish = async (value: ProgramEvent) => {
    if (value?.status === 'live' && programId) {
      const client = new EventsApi(generateApiConfig())
      try {
        await requestHandler<ProgramEvent>(
          () => client.unpublishProgramEvent({ program_id: programId, event_id: value.id }),
          {
            toast: {
              success: (translations as any)['success.unpublish_event_message'],
              setToast,
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'program_event', program_id: programId }),
        )
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.unpublish_event_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleBulkUnpublish = async (values: ProgramEvent[], update_all?: boolean) => {
    if (programId && (values.length > 0 || update_all)) {
      const client = new EventsApi(generateApiConfig())
      try {
        const operation = await requestHandler<BatchProgramEventOperation>(() =>
          client.unpublishBulkProgramEvent({
            program_id: programId,
            ProgramEventBulkParams: {
              update_all,
              event_ids: !update_all ? values.map((e) => e.id) : [],
            },
            search: search || undefined,
            age_lt: filter?.age?.lt,
            age_gt: filter?.age?.gt,
            age_lte: filter?.age?.lte,
            age_gte: filter?.age?.gte,
            gender: filter?.gender && filter?.gender.length > 0 ? filter.gender : undefined,
            venue_city:
              filter?.venue_city && filter?.venue_city.length > 0 ? filter.venue_city : undefined,
            online_event:
              typeof filter?.online_event !== 'undefined' ? filter?.online_event : undefined,
            status: filter?.status && filter?.status.length > 0 ? filter.status : undefined,
            type: filter?.type && filter?.type.length > 0 ? filter.type : undefined,
            tags: filter?.tags && filter?.tags.length > 0 ? filter.tags : undefined,
            id: filter?.id && filter?.id.length > 0 ? filter.id : undefined,
            is_appointment:
              typeof filter?.is_appointment !== 'undefined' ? filter.is_appointment : undefined,
            capacity_lt: filter?.capacity?.lt,
            capacity_gt: filter?.capacity?.gt,
            capacity_lte: filter?.capacity?.lte,
            capacity_gte: filter?.capacity?.gte,
            scheduled_lt:
              filter?.scheduled?.lt &&
              !filter?.scheduled_type?.includes('is upcoming') &&
              !filter?.scheduled_type?.includes('is past')
                ? new Date(filter?.scheduled?.lt)
                : undefined,
            scheduled_gt:
              filter?.scheduled?.gt &&
              !filter?.scheduled_type?.includes('is upcoming') &&
              !filter?.scheduled_type?.includes('is past')
                ? new Date(filter?.scheduled?.gt)
                : undefined,
            scheduled_lte: filter?.scheduled_type?.includes('is past')
              ? new Date()
              : filter?.scheduled?.lte
              ? new Date(filter?.scheduled?.lte)
              : undefined,
            scheduled_gte: filter?.scheduled_type?.includes('is upcoming')
              ? new Date()
              : filter?.scheduled?.gte
              ? new Date(filter?.scheduled?.gte)
              : undefined,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'program_event', program_id: programId }),
        )
        if (operation.errored_operations === 0) {
          setToast?.({
            header: (translations as any)['success.default_toast_header'],
            message: (translations as any)['success.default_toast_message'],
            type: 'success',
          })
        } else {
          setToast?.({
            header: (translations as any)['error.default_toast_header'],
            message: (translations as any)['error.update_bulk_events'],
            type: 'danger',
          })
        }
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.default_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleCancel = async (value: ProgramEvent) => {
    if (programId) {
      const client = new EventsApi(generateApiConfig())
      try {
        await requestHandler<ProgramEvent>(
          () => client.cancelProgramEvent({ program_id: programId, event_id: value.id }),
          {
            toast: {
              success: (translations as any)['success.cancel_event_message'],
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'program_event', program_id: programId }),
        )
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.cancel_event_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleBulkCancel = async (values: ProgramEvent[], update_all?: boolean) => {
    if (programId && (values.length > 0 || update_all)) {
      const client = new EventsApi(generateApiConfig())
      try {
        const operation = await requestHandler<BatchProgramEventOperation>(() =>
          client.cancelBulkProgramEvent({
            program_id: programId,
            ProgramEventBulkParams: {
              update_all,
              event_ids: !update_all ? values.map((e) => e.id) : [],
            },
            search: search || undefined,
            age_lt: filter?.age?.lt,
            age_gt: filter?.age?.gt,
            age_lte: filter?.age?.lte,
            age_gte: filter?.age?.gte,
            gender: filter?.gender && filter?.gender.length > 0 ? filter.gender : undefined,
            venue_city:
              filter?.venue_city && filter?.venue_city.length > 0 ? filter.venue_city : undefined,
            online_event:
              typeof filter?.online_event !== 'undefined' ? filter?.online_event : undefined,
            status: filter?.status && filter?.status.length > 0 ? filter.status : undefined,
            type: filter?.type && filter?.type.length > 0 ? filter.type : undefined,
            tags: filter?.tags && filter?.tags.length > 0 ? filter.tags : undefined,
            id: filter?.id && filter?.id.length > 0 ? filter.id : undefined,
            is_appointment:
              typeof filter?.is_appointment !== 'undefined' ? filter.is_appointment : undefined,
            capacity_lt: filter?.capacity?.lt,
            capacity_gt: filter?.capacity?.gt,
            capacity_lte: filter?.capacity?.lte,
            capacity_gte: filter?.capacity?.gte,
            scheduled_lt:
              filter?.scheduled?.lt &&
              !filter?.scheduled_type?.includes('is upcoming') &&
              !filter?.scheduled_type?.includes('is past')
                ? new Date(filter?.scheduled?.lt)
                : undefined,
            scheduled_gt:
              filter?.scheduled?.gt &&
              !filter?.scheduled_type?.includes('is upcoming') &&
              !filter?.scheduled_type?.includes('is past')
                ? new Date(filter?.scheduled?.gt)
                : undefined,
            scheduled_lte: filter?.scheduled_type?.includes('is past')
              ? new Date()
              : filter?.scheduled?.lte
              ? new Date(filter?.scheduled?.lte)
              : undefined,
            scheduled_gte: filter?.scheduled_type?.includes('is upcoming')
              ? new Date()
              : filter?.scheduled?.gte
              ? new Date(filter?.scheduled?.gte)
              : undefined,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'program_event', program_id: programId }),
        )
        if (operation.errored_operations === 0) {
          setToast?.({
            header: (translations as any)['success.default_toast_header'],
            message: (translations as any)['success.default_toast_message'],
            type: 'success',
          })
        } else {
          setToast?.({
            header: (translations as any)['error.default_toast_header'],
            message: (translations as any)['error.update_bulk_events'],
            type: 'danger',
          })
        }
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.default_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleDelete = async (value: ProgramEvent) => {
    if (programId) {
      const programEventClient = new EventsApi(generateApiConfig())
      try {
        await requestHandler(
          () =>
            programEventClient.deleteProgramEvent({ program_id: programId, event_id: value.id }),
          {
            toast: {
              success: (translations as any)['success.delete_event_message'],
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'program_event', program_id: programId }),
        )
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.delete_event_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleBulkDelete = async (values: ProgramEvent[], update_all?: boolean) => {
    if (programId && (values.length > 0 || update_all)) {
      const client = new EventsApi(generateApiConfig())
      try {
        const operation = await requestHandler<BatchProgramEventOperation>(() =>
          client.deleteBulkProgramEvent({
            program_id: programId,
            ProgramEventBulkParams: {
              update_all,
              event_ids: !update_all ? values.map((e) => e.id) : [],
            },
            search: search || undefined,
            age_lt: filter?.age?.lt,
            age_gt: filter?.age?.gt,
            age_lte: filter?.age?.lte,
            age_gte: filter?.age?.gte,
            gender: filter?.gender && filter?.gender.length > 0 ? filter.gender : undefined,
            venue_city:
              filter?.venue_city && filter?.venue_city.length > 0 ? filter.venue_city : undefined,
            online_event:
              typeof filter?.online_event !== 'undefined' ? filter?.online_event : undefined,
            status: filter?.status && filter?.status.length > 0 ? filter.status : undefined,
            type: filter?.type && filter?.type.length > 0 ? filter.type : undefined,
            tags: filter?.tags && filter?.tags.length > 0 ? filter.tags : undefined,
            id: filter?.id && filter?.id.length > 0 ? filter.id : undefined,
            is_appointment:
              typeof filter?.is_appointment !== 'undefined' ? filter.is_appointment : undefined,
            capacity_lt: filter?.capacity?.lt,
            capacity_gt: filter?.capacity?.gt,
            capacity_lte: filter?.capacity?.lte,
            capacity_gte: filter?.capacity?.gte,
            scheduled_lt:
              filter?.scheduled?.lt &&
              !filter?.scheduled_type?.includes('is upcoming') &&
              !filter?.scheduled_type?.includes('is past')
                ? new Date(filter?.scheduled?.lt)
                : undefined,
            scheduled_gt:
              filter?.scheduled?.gt &&
              !filter?.scheduled_type?.includes('is upcoming') &&
              !filter?.scheduled_type?.includes('is past')
                ? new Date(filter?.scheduled?.gt)
                : undefined,
            scheduled_lte: filter?.scheduled_type?.includes('is past')
              ? new Date()
              : filter?.scheduled?.lte
              ? new Date(filter?.scheduled?.lte)
              : undefined,
            scheduled_gte: filter?.scheduled_type?.includes('is upcoming')
              ? new Date()
              : filter?.scheduled?.gte
              ? new Date(filter?.scheduled?.gte)
              : undefined,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'program_event', program_id: programId }),
        )
        if (operation.errored_operations === 0) {
          setToast?.({
            header: (translations as any)['success.default_toast_header'],
            message: (translations as any)['success.default_toast_message'],
            type: 'success',
          })
        } else {
          setToast?.({
            header: (translations as any)['error.default_toast_header'],
            message: (translations as any)['error.update_bulk_events'],
            type: 'danger',
          })
        }
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.default_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handlePatch = async (eventId: string, value: ProgramEventPatchParams) => {
    if (programId) {
      const programEventClient = new EventsApi(generateApiConfig())
      try {
        await requestHandler(
          () =>
            programEventClient.patchProgramEvent({
              program_id: programId,
              event_id: eventId,
              ProgramEventPatchParams: value,
            }),
          {
            toast: {
              success: (translations as any)['success.update_event_message'],
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'program_event', program_id: programId }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'registration_option', program_id: programId }),
        )
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.update_event_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  const handleBulkPatch = async (
    values: ProgramEventBulkParams,
  ): Promise<BatchProgramEventOperation | undefined> => {
    if (programId && ((values.event_ids && values.event_ids.length > 0) || values.update_all)) {
      const client = new EventsApi(generateApiConfig())
      try {
        const operation = await requestHandler<BatchProgramEventOperation>(() =>
          client.patchBulkProgramEvent({
            program_id: programId,
            ProgramEventBulkParams: values,
            search: search || undefined,
            age_lt: filter?.age?.lt,
            age_gt: filter?.age?.gt,
            age_lte: filter?.age?.lte,
            age_gte: filter?.age?.gte,
            gender: filter?.gender && filter?.gender.length > 0 ? filter.gender : undefined,
            venue_city:
              filter?.venue_city && filter?.venue_city.length > 0 ? filter.venue_city : undefined,
            online_event:
              typeof filter?.online_event !== 'undefined' ? filter?.online_event : undefined,
            status: filter?.status && filter?.status.length > 0 ? filter.status : undefined,
            type: filter?.type && filter?.type.length > 0 ? filter.type : undefined,
            tags: filter?.tags && filter?.tags.length > 0 ? filter.tags : undefined,
            id: filter?.id && filter?.id.length > 0 ? filter.id : undefined,
            is_appointment:
              typeof filter?.is_appointment !== 'undefined' ? filter.is_appointment : undefined,
            capacity_lt: filter?.capacity?.lt,
            capacity_gt: filter?.capacity?.gt,
            capacity_lte: filter?.capacity?.lte,
            capacity_gte: filter?.capacity?.gte,
            scheduled_lt:
              filter?.scheduled?.lt &&
              !filter?.scheduled_type?.includes('is upcoming') &&
              !filter?.scheduled_type?.includes('is past')
                ? new Date(filter?.scheduled?.lt)
                : undefined,
            scheduled_gt:
              filter?.scheduled?.gt &&
              !filter?.scheduled_type?.includes('is upcoming') &&
              !filter?.scheduled_type?.includes('is past')
                ? new Date(filter?.scheduled?.gt)
                : undefined,
            scheduled_lte: filter?.scheduled_type?.includes('is past')
              ? new Date()
              : filter?.scheduled?.lte
              ? new Date(filter?.scheduled?.lte)
              : undefined,
            scheduled_gte: filter?.scheduled_type?.includes('is upcoming')
              ? new Date()
              : filter?.scheduled?.gte
              ? new Date(filter?.scheduled?.gte)
              : undefined,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'program_event', program_id: programId }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'registration_option', program_id: programId }),
        )
        return operation
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.default_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  return {
    handlePublish,
    handleBulkPublish,
    handleUnpublish,
    handleBulkUnpublish,
    handleCancel,
    handleBulkCancel,
    handleDelete,
    handleBulkDelete,
    handlePatch,
    handleBulkPatch,
  }
}
